import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CalendarCheck, Clock, MapPin, Phone, Smartphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { vals } from "@/lib/format";
import { doctorHours } from "@/lib/clinic";
import { FileImage } from "@/components/files/FileFields";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReportActions } from "@/components/tpv/ZReport";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const dayRange = (days, t) => {
  const s = [...days].sort((a, b) => a - b);
  if (!s.length) return "";
  const consecutive = s.every((d, i) => i === 0 || d === s[i - 1] + 1);
  return consecutive && s.length > 2 ? `${t(`day_${DAYS[s[0]]}`)} – ${t(`day_${DAYS[s[s.length - 1]]}`)}` : s.map((d) => t(`day_${DAYS[d]}`)).join(", ");
};

export function ClinicPosterDialog({ link, onClose }) {
  const { t, view } = useApp();
  const ref = useRef(null);
  const s = view.shop || {};
  const doctors = vals(view.staff).filter((x) => x.active !== false && ["doctor", "owner"].includes(x.role));
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}><DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="clinic-poster-dialog">
      <DialogHeader><DialogTitle>{t("poster_title")}</DialogTitle></DialogHeader>
      <div ref={ref} dir={document.documentElement.dir} className="mx-auto bg-white text-[#12384A] w-[595px] min-h-[842px] p-10 flex flex-col relative overflow-hidden" data-testid="clinic-poster" style={{ fontFamily: "inherit" }}>
        <div className="absolute inset-x-0 top-0 h-3 bg-[#12465E]" /><div className="absolute inset-x-0 bottom-0 h-3 bg-[#E9B949]" />
        <div className="flex items-center gap-4">
          {s.logo_id ? <FileImage id={s.logo_id} className="w-24 h-24 object-contain" alt={s.name} /> : <span className="w-24 h-24 rounded-2xl bg-[#12465E] text-white grid place-items-center font-black text-4xl">{(s.name || "O").slice(0, 1)}</span>}
          <div className="min-w-0"><div className="font-heading text-3xl leading-tight break-words">{s.name}</div><div className="text-sm text-[#5b6b75] uppercase tracking-[.2em]">{t("type_clinic")}</div></div>
        </div>
        <div className="mt-8 text-center">
          <h1 className="font-heading text-4xl leading-tight">{t("poster_headline")}</h1>
          <p className="mt-2 text-lg text-[#5b6b75]">{t("poster_sub")}</p>
        </div>
        <div className="mt-8 flex items-center justify-center"><div className="p-4 rounded-3xl border-4 border-[#12465E]"><QRCodeSVG value={link} size={260} level="H" /></div></div>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs">
          {[[Smartphone, "poster_step1"], [CalendarCheck, "poster_step2"], [Clock, "poster_step3"]].map(([I, k], i) => (
            <div key={k} className="rounded-xl bg-[#F4F7FB] p-3"><span className="mx-auto w-9 h-9 rounded-full bg-[#E9B949] text-[#1A1A1A] grid place-items-center font-black">{i + 1}</span><I className="w-5 h-5 mx-auto mt-2 text-[#12465E]" /><div className="mt-1 font-semibold leading-snug">{t(k)}</div></div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-[#dbe6ef] p-4">
          <div className="font-bold flex items-center gap-2 mb-2"><Clock className="w-4 h-4" />{t("working_hours")}</div>
          {!doctors.length && <p className="text-sm text-[#5b6b75]">—</p>}
          <table className="w-full text-sm">
            <tbody>{doctors.map((d) => { const h = doctorHours(d); return (
              <tr key={d.id} className="border-t border-[#eef2f6] first:border-0"><td className="py-1.5 font-semibold">{d.name}{d.specialty ? <span className="text-[#5b6b75] font-normal"> · {d.specialty}</span> : ""}</td><td className="py-1.5 text-[#5b6b75]">{dayRange(h.days || [], t)}</td><td className="py-1.5 text-end font-bold" dir="ltr">{h.start} – {h.end}</td></tr>
            ); })}</tbody>
          </table>
        </div>
        <div className="mt-auto pt-6 flex items-end justify-between text-sm text-[#5b6b75]">
          <div className="space-y-1">{s.address && <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{s.address}</div>}{s.phone && <div className="flex items-center gap-1.5" dir="ltr"><Phone className="w-4 h-4" />{s.phone}</div>}</div>
          <div className="text-end"><div className="text-[11px]" dir="ltr">{link}</div><div className="text-[10px] mt-1">{t("operated_by")} <b>Orbit PMS</b></div></div>
        </div>
      </div>
      <ReportActions target={ref} name={`poster-${(s.name || "clinic").replace(/\s+/g, "-")}`} wide />
    </DialogContent></Dialog>
  );
}
